import { useState, useEffect, useCallback } from 'react';
import { Shield, CheckCircle, XCircle, Clock, RefreshCw, AlertTriangle } from 'lucide-react';

type RevocationProduct = 'hub' | 'psa' | 'rmm' | 'voice' | 'defend' | 'email';
type RevocationStatus = 'pending' | 'revoked' | 'failed' | 'skipped';

interface ProductRevocationState {
  product: RevocationProduct;
  status: RevocationStatus;
  error?: string;
  revoked_at?: string;
}

interface RevocationDoc {
  id: string;
  employee_name: string;
  employee_email: string;
  products: ProductRevocationState[];
  overall_status: 'pending' | 'in_progress' | 'completed' | 'partial' | 'failed';
  started_at: string;
  completed_at?: string;
}

const PRODUCT_LABELS: Record<RevocationProduct, string> = {
  hub: 'Hub SSO Account',
  psa: 'PSA Technician',
  rmm: 'RMM Agent Policies',
  voice: 'Voice Extension',
  defend: 'Defend Risk Profile',
  email: 'Email Forwarding',
};

const PRODUCT_ICONS: Record<RevocationProduct, string> = {
  hub: '🏠',
  psa: '🎫',
  rmm: '💻',
  voice: '📞',
  defend: '🛡️',
  email: '📧',
};

function StatusIcon({ status }: { status: RevocationStatus }) {
  switch (status) {
    case 'revoked': return <CheckCircle className="w-4 h-4 text-green-400" />;
    case 'failed': return <XCircle className="w-4 h-4 text-red-400" />;
    case 'skipped': return <AlertTriangle className="w-4 h-4 text-muted-foreground" />;
    default: return <Clock className="w-4 h-4 text-muted-foreground" />;
  }
}

function OverallBadge({ status }: { status: RevocationDoc['overall_status'] }) {
  const styles: Record<string, string> = {
    completed: 'bg-green-500/20 text-green-400',
    partial: 'bg-amber-500/20 text-amber-400',
    failed: 'bg-red-500/20 text-red-400',
    in_progress: 'bg-blue-500/20 text-blue-400',
    pending: 'bg-muted/20 text-muted-foreground',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${styles[status] ?? styles['pending']}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

interface Props {
  offboardingId: string;
}

export function RevocationStatusPanel({ offboardingId }: Props) {
  const [revocation, setRevocation] = useState<RevocationDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [retrying, setRetrying] = useState<RevocationProduct | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/offboarding/${offboardingId}/revocation-status`);
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json() as { revocation: RevocationDoc | null };
      setRevocation(data.revocation);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [offboardingId]);

  useEffect(() => { void load(); }, [load]);

  async function startRevocation() {
    setStarting(true);
    setError(null);
    try {
      const res = await fetch(`/api/offboarding/${offboardingId}/revoke-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('Failed to start revocation');
      const data = await res.json() as RevocationDoc;
      setRevocation(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setStarting(false);
    }
  }

  async function retryProduct(product: RevocationProduct) {
    setRetrying(product);
    setError(null);
    try {
      const res = await fetch(`/api/offboarding/${offboardingId}/revocation/${product}/retry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('Failed to retry');
      const data = await res.json() as RevocationDoc;
      setRevocation(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRetrying(null);
    }
  }

  if (loading) return (
    <div className="glass rounded-xl p-5">
      <div className="text-sm text-muted-foreground">Loading access revocation status...</div>
    </div>
  );

  return (
    <div className="glass rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-red-400" />
          <h3 className="text-sm font-semibold text-foreground">Access Revocation</h3>
        </div>
        {revocation && <OverallBadge status={revocation.overall_status} />}
      </div>

      {error && <p className="text-red-400 text-sm">{error}</p>}

      {!revocation ? (
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
            <p className="text-xs text-red-300">
              This will immediately revoke the employee's access across Hub SSO, PSA, RMM, Voice, and Defend.
              A bus event will notify all products to terminate active sessions.
            </p>
          </div>
          <button
            onClick={() => void startRevocation()}
            disabled={starting}
            className="flex items-center gap-2 w-full justify-center px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30 text-sm disabled:opacity-50"
          >
            <Shield className="w-4 h-4" />
            {starting ? 'Revoking Access...' : 'Revoke All Access'}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {revocation.products.map(productState => (
            <div key={productState.product} className="flex items-center gap-3 p-3 rounded-lg bg-surface-3">
              <span className="text-base">{PRODUCT_ICONS[productState.product]}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <StatusIcon status={productState.status} />
                  <span className="text-sm font-medium text-foreground">
                    {PRODUCT_LABELS[productState.product]}
                  </span>
                </div>
                {productState.status === 'revoked' && productState.revoked_at && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Revoked {new Date(productState.revoked_at).toLocaleTimeString()}
                  </p>
                )}
                {productState.status === 'skipped' && productState.error && (
                  <p className="text-xs text-muted-foreground mt-0.5">{productState.error}</p>
                )}
                {productState.status === 'failed' && productState.error && (
                  <p className="text-xs text-red-400 mt-0.5">{productState.error}</p>
                )}
              </div>
              {productState.status === 'failed' && (
                <button
                  onClick={() => void retryProduct(productState.product)}
                  disabled={retrying === productState.product}
                  className="flex items-center gap-1 px-2.5 py-1 rounded text-xs bg-red-500/10 text-red-400 hover:bg-red-500/20 disabled:opacity-50"
                >
                  <RefreshCw className={`w-3 h-3 ${retrying === productState.product ? 'animate-spin' : ''}`} />
                  Retry
                </button>
              )}
            </div>
          ))}

          {revocation.completed_at && (
            <p className="text-xs text-muted-foreground text-right">
              Completed {new Date(revocation.completed_at).toLocaleString()}
            </p>
          )}

          {revocation.overall_status === 'partial' && (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-xs text-amber-400">
                Partial revocation — some products failed. Retry failed items or manually revoke access.
              </p>
            </div>
          )}

          {revocation.overall_status === 'completed' && (
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <p className="text-xs text-green-400">
                All access successfully revoked. Employee has been removed from all integrated products.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
